import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Search,
  SwapVert,
  ViewModule,
  TableChart,
  Add,
} from "@material-ui/icons";

import {
  GOVERNING_BODY_SORT_FIELDS,
  GOVERNING_BODY_SORT_OPTIONS,
  GOVERNING_BODY_TEAM_TYPE_VALUES,
  MIN_SEARCH_KEY_LENGTH,
  TEAM_TYPE_OPTIONS,
  TIME_OUT_REQUEST,
} from "../../constants/data";

const useStyles = makeStyles({
  root: {
    backgroundColor: "#ffffff",
    border: "1px solid #d8e0ea",
    borderRadius: "10px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "minmax(240px, 2fr) repeat(3, minmax(120px, 1fr)) auto",
    gap: "12px",
    alignItems: "end",
    "@media (max-width: 1200px)": {
      gridTemplateColumns: "repeat(2, minmax(180px, 1fr))",
    },
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  },
  searchInput: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#ffffff",
    },
  },
  rowFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  sortWrap: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  sortLabel: {
    fontWeight: 700,
    color: "#101828",
  },
  sortControl: {
    minWidth: "190px",
  },
  toggleGroup: {
    border: "1px solid #d8e0ea",
    borderRadius: "8px",
    overflow: "hidden",
  },
  toggleButton: {
    textTransform: "none",
    fontWeight: 600,
    border: "none",
  },
  selectedToggleButton: {
    backgroundColor: "#eaf2ff",
    color: "#0d6efd",
  },
  createButton: {
    textTransform: "none",
    fontWeight: 700,
    minHeight: "40px",
    backgroundColor: "#0d6efd",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "#084fc7",
    },
  },
});

const STATUS_OPTIONS = ["ALL", "ACTIVE", "INACTIVE", "DEPRECATED"];

// Governing bodies are never type "G" themselves; offer the same H/U/C/F/S set
// the create/edit form allows.
const TEAM_TYPE_FILTER_OPTIONS = TEAM_TYPE_OPTIONS.filter((option) =>
  GOVERNING_BODY_TEAM_TYPE_VALUES.includes(option.value),
);

const GoverningBodyFilters = ({
  filters,
  pagination,
  viewMode,
  ddLists,
  onFiltersChange,
  onSortChange,
  onViewModeChange,
  onCreate,
}) => {
  const classes = useStyles();
  const searchTimeoutRef = useRef(null);
  const [searchValue, setSearchValue] = useState(filters?.query || "");

  useEffect(() => {
    setSearchValue(filters?.query || "");
  }, [filters?.query]);

  useEffect(
    () => () => {
      clearTimeout(searchTimeoutRef.current);
    },
    [],
  );

  const handleSearchChange = (event) => {
    const value = event.target.value || "";
    setSearchValue(value);

    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      if (value.length > 0 && value.length < MIN_SEARCH_KEY_LENGTH) {
        return;
      }
      onFiltersChange({ query: value });
    }, TIME_OUT_REQUEST);
  };

  const handleStatusChange = (event) => {
    onFiltersChange({ status: event.target.value });
  };

  const handleTeamTypeChange = (event) => {
    onFiltersChange({ teamType: event.target.value });
  };

  const handleSortFieldChange = (event) => {
    const key = event.target.value;
    const sort = GOVERNING_BODY_SORT_FIELDS[key] || "Name";
    onSortChange({ sort });
  };

  const activeSortKey = Object.keys(GOVERNING_BODY_SORT_FIELDS).find(
    (key) => GOVERNING_BODY_SORT_FIELDS[key] === pagination?.sort,
  );

  return (
    <Box className={classes.root}>
      <Box className={classes.row}>
        <TextField
          variant="outlined"
          size="small"
          label="Search"
          value={searchValue}
          onChange={handleSearchChange}
          className={classes.searchInput}
          placeholder="Search by name, abbreviation, short name..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl variant="outlined" size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={filters?.status || "ACTIVE"}
            onChange={handleStatusChange}
            label="Status"
          >
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small">
          <InputLabel>Team type</InputLabel>
          <Select
            value={filters?.teamType || "ALL"}
            onChange={handleTeamTypeChange}
            label="Team type"
          >
            <MenuItem value="ALL">ALL</MenuItem>
            {TEAM_TYPE_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small">
          <InputLabel>DD list</InputLabel>
          <Select
            value={filters?.ddListId ?? "ALL"}
            onChange={(event) =>
              onFiltersChange({
                ddListId:
                  event.target.value === "ALL" ? null : Number(event.target.value),
              })
            }
            label="DD list"
          >
            <MenuItem value="ALL">ALL</MenuItem>
            {(ddLists || [])
              .slice()
              .sort((a, b) =>
                String(a?.abbreviation || "").localeCompare(
                  String(b?.abbreviation || ""),
                  undefined,
                  { sensitivity: "base" },
                ),
              )
              .map((dd) => (
                <MenuItem key={dd.id} value={dd.id}>
                  {dd.abbreviation || `DD List ${dd.id}`}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <Button
          type="button"
          variant="contained"
          className={classes.createButton}
          onClick={onCreate}
          startIcon={<Add />}
        >
          Create governing body
        </Button>
      </Box>

      <Box className={classes.rowFooter}>
        <Box className={classes.sortWrap}>
          <Typography className={classes.sortLabel}>Sort by</Typography>
          <FormControl variant="outlined" size="small" className={classes.sortControl}>
            <Select
              value={activeSortKey || "name"}
              onChange={handleSortFieldChange}
            >
              {GOVERNING_BODY_SORT_OPTIONS.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            aria-label="Toggle sort direction"
            onClick={() =>
              onSortChange({ isAscending: !Boolean(pagination?.isAscending) })
            }
          >
            <SwapVert fontSize="small" />
          </IconButton>
        </Box>

        <ButtonGroup className={classes.toggleGroup}>
          <Button
            className={`${classes.toggleButton} ${
              viewMode === "card" ? classes.selectedToggleButton : ""
            }`}
            onClick={() => onViewModeChange("card")}
          >
            <ViewModule fontSize="small" />
            <Box component="span" ml={1}>
              Card view
            </Box>
          </Button>
          <Button
            className={`${classes.toggleButton} ${
              viewMode === "table" ? classes.selectedToggleButton : ""
            }`}
            onClick={() => onViewModeChange("table")}
          >
            <TableChart fontSize="small" />
            <Box component="span" ml={1}>
              Table view
            </Box>
          </Button>
        </ButtonGroup>
      </Box>
    </Box>
  );
};

GoverningBodyFilters.propTypes = {
  filters: PropTypes.shape({
    status: PropTypes.string,
    teamType: PropTypes.string,
    query: PropTypes.string,
    locale: PropTypes.string,
    ddListId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  pagination: PropTypes.shape({
    sort: PropTypes.string,
    isAscending: PropTypes.bool,
  }),
  viewMode: PropTypes.oneOf(["card", "table"]),
  ddLists: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      abbreviation: PropTypes.string,
    }),
  ),
  onFiltersChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onViewModeChange: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};

GoverningBodyFilters.defaultProps = {
  filters: {
    status: "ACTIVE",
    teamType: "ALL",
    query: "",
    locale: "en-US",
    ddListId: null,
  },
  pagination: {
    sort: "Name",
    isAscending: true,
  },
  viewMode: "card",
  ddLists: [],
};

export default GoverningBodyFilters;
